import { daysInAYear, heatCapWaterKWh } from "./constants";
import { InsufficientCover, SolarDiverterForm } from "./form";

export interface ComputedValues<T> {
  totalHotWaterReq: T;
  avgCWTemp: T;
  heatDelta: T;
  heatEnergyPerL: T;
  heatEnergyPerYear: T;
  gasReqPerYear: T;
  totalCostNoDiverter: T;
  totalInvestment: T;
  daysSufficientSolar: T;
  gasPerDayCover: T;
  gasPerYearCover: T;
  electricityPerDayCover: T;
  electricityPerYearCover: T;
  electricityRequiredPerDay: T;
  electricityNotExported: T;
  lostExportRevenue: T;
  totalDiverterCost: T;
  paybackPeriod: T;
  annualSavingsThereafter: T;
}

const currencyOptions = {
  style: "currency",
  currency: "GBP",
} as const;

export const computeValues = (
  form: SolarDiverterForm
): ComputedValues<number> => {
  const totalHotWaterReq =
    Number(form.hotWaterReq) * Number(form.familyMembers) * daysInAYear;
  const avgCWTemp = (Number(form.minCWTemp) + Number(form.maxCWTemp)) / 2;
  const heatDelta = Number(form.reqHWTemp) - avgCWTemp;
  const heatEnergyPerL = heatDelta * heatCapWaterKWh;
  const heatEnergyPerYear = heatEnergyPerL * totalHotWaterReq;
  const gasReqPerYear =
    heatEnergyPerYear / (Number(form.boilerEfficiency) / 100);
  const totalCostNoDiverter = gasReqPerYear * Number(form.gasCost);
  const totalInvestment =
    Number(form.solarDiverterCost) + Number(form.installationCost);
  const daysSufficientSolar = daysInAYear - Number(form.daysInsufficientSolar);
  const gasPerDayCover = totalCostNoDiverter / daysInAYear;
  const gasPerYearCover = gasPerDayCover * Number(form.daysInsufficientSolar);
  const electricityPerDayCover =
    (heatEnergyPerYear / daysInAYear) * Number(form.electricityCost);
  const electricityPerYearCover =
    electricityPerDayCover * Number(form.daysInsufficientSolar);
  const electricityRequiredPerDay = heatEnergyPerYear / daysInAYear;
  const electricityNotExported =
    daysSufficientSolar * electricityRequiredPerDay;
  const lostExportRevenue =
    Number(form.electricityExportPrice) * electricityNotExported;
  const totalDiverterCost =
    (Number(form.insufficientCoverType) === InsufficientCover.Gas
      ? gasPerYearCover
      : electricityPerYearCover) + lostExportRevenue;
  const paybackPeriod = getPaybackPeriod(
    totalCostNoDiverter,
    totalDiverterCost,
    totalInvestment
  );
  const annualSavingsThereafter = Math.max(
    totalCostNoDiverter - totalDiverterCost,
    0
  );

  return {
    totalHotWaterReq,
    avgCWTemp,
    heatDelta,
    heatEnergyPerL,
    heatEnergyPerYear,
    gasReqPerYear,
    totalCostNoDiverter,
    totalInvestment,
    daysSufficientSolar,
    gasPerDayCover,
    gasPerYearCover,
    electricityPerDayCover,
    electricityPerYearCover,
    electricityRequiredPerDay,
    electricityNotExported,
    lostExportRevenue,
    totalDiverterCost,
    paybackPeriod,
    annualSavingsThereafter,
  };
};

export const formatComputedValues = (
  values: ComputedValues<number>
): ComputedValues<string> => {
  return {
    totalHotWaterReq: formatNumber(values.totalHotWaterReq),
    avgCWTemp: formatNumber(values.avgCWTemp),
    heatDelta: formatNumber(values.heatDelta),
    heatEnergyPerL: formatNumber(values.heatEnergyPerL, {
      maximumFractionDigits: 5,
    }),
    heatEnergyPerYear: formatNumber(values.heatEnergyPerYear),
    gasReqPerYear: formatNumber(values.gasReqPerYear),
    totalCostNoDiverter: formatNumber(
      values.totalCostNoDiverter,
      currencyOptions
    ),
    totalInvestment: formatNumber(values.totalInvestment, currencyOptions),
    daysSufficientSolar: formatNumber(values.daysSufficientSolar),
    gasPerDayCover: formatNumber(values.gasPerDayCover, currencyOptions),
    gasPerYearCover: formatNumber(values.gasPerYearCover, currencyOptions),
    electricityPerDayCover: formatNumber(
      values.electricityPerDayCover,
      currencyOptions
    ),
    electricityPerYearCover: formatNumber(
      values.electricityPerYearCover,
      currencyOptions
    ),
    electricityRequiredPerDay: formatNumber(values.electricityRequiredPerDay),
    electricityNotExported: formatNumber(values.electricityNotExported),
    lostExportRevenue: formatNumber(values.lostExportRevenue, currencyOptions),
    totalDiverterCost: formatNumber(values.totalDiverterCost, currencyOptions),
    paybackPeriod: Number.isFinite(values.paybackPeriod)
      ? formatNumber(values.paybackPeriod, {
          maximumFractionDigits: 1,
        })
      : "Never",
    annualSavingsThereafter: formatNumber(
      values.annualSavingsThereafter,
      currencyOptions
    ),
  };
};

const formatNumber = (
  value: number,
  options: Intl.NumberFormatOptions = {}
): string => {
  return new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 2,
    ...options,
  }).format(value);
};

const getPaybackPeriod = (
  totalCostNoDiverter: number,
  totalDiverterCost: number,
  totalInvestment: number
): number => {
  const costDelta = totalCostNoDiverter - totalDiverterCost;
  if (costDelta > 0) {
    return totalInvestment / costDelta;
  } else {
    return Infinity;
  }
};
